import React, { useEffect, useState } from "react";
import { supabase } from "../services/supabaseClient";
import { useLanguage } from "../contexts/LanguageContext";

interface DictionaryDefinition {
  definition?: string;
  example?: string;
  synonyms?: string[];
  antonyms?: string[];
}

interface DictionaryMeaning {
  partOfSpeech?: string;
  definitions?: DictionaryDefinition[];
  synonyms?: string[];
  antonyms?: string[];
}

interface DictionaryLookupData {
  word?: string;
  matched_word?: string;
  phonetic?: string;
  pronunciations?: Array<{
    text?: string;
    audio_url?: string;
  }>;
  meanings?: DictionaryMeaning[];
  sourceUrls?: string[];
  definition_source?: string;
  message?: string;
  error?: string;
}

interface VocabItem {
  word: string;
  created_at: string;
  updated_at: string;
  search_count: number;
  definitionData?: DictionaryLookupData;
  definitionError?: string;
  definitionLoading?: boolean;
  removing?: boolean;
  removeError?: string;
}

interface SavedVocabProps {
  userId: string;
}

const getDefinitionSourceLabel = (source: string | undefined): string => {
  if (source === "merriam_webster") return "Merriam-Webster";
  if (source === "free_dictionary") return "Free Dictionary";
  return "Dictionary";
};

const getUniqueTerms = (values: Array<string | undefined> = []): string[] =>
  Array.from(new Set(values.filter(Boolean) as string[])).slice(0, 6);

const getDefinitionErrorMessage = async (error: any): Promise<string> => {
  const fallback = error?.message || "Definition not available";

  try {
    if (error?.context?.json) {
      const body = await error.context.json();
      return body?.message || body?.error || fallback;
    }
  } catch {
    return fallback;
  }

  return fallback;
};

const VocabDetail: React.FC<{ data: DictionaryLookupData }> = ({ data }) => {
  const { t } = useLanguage();
  const pronunciations = data.pronunciations || [];
  const audioUrl = pronunciations.find((item) => item.audio_url)?.audio_url;
  const firstSourceUrl = data.sourceUrls?.[0];

  return (
    <div className="mt-4 border-t border-gray-100 pt-4 text-sm text-gray-700">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 flex-wrap items-baseline gap-2">
          <strong className="text-lg text-indigo-800">
            {data.word || data.matched_word}
          </strong>
          {data.matched_word && data.matched_word !== data.word && (
            <span className="text-xs text-gray-500">
              {t("vocab.matched", { word: data.matched_word })}
            </span>
          )}
          <span className="text-xs font-medium text-gray-500">
            {getDefinitionSourceLabel(data.definition_source)}
          </span>
        </div>

        {(data.phonetic || pronunciations.length > 0) && (
          <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
            {audioUrl && (
              <button
                type="button"
                onClick={() => new Audio(audioUrl).play()}
                className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-indigo-600 text-xs text-white hover:bg-indigo-700"
                title={t("vocab.playPronunciation")}
                aria-label={t("vocab.playPronunciation")}
              >
                ▶
              </button>
            )}
            {getUniqueTerms([
              data.phonetic,
              ...pronunciations.map((item) => item.text),
            ]).slice(0, 2).map((text) => (
              <span
                key={text}
                className="rounded bg-indigo-50 px-2 py-1 text-xs font-medium text-indigo-700"
              >
                {text}
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="space-y-4">
        {(data.meanings || []).slice(0, 2).map((meaning, meaningIndex) => {
          const synonyms = getUniqueTerms([
            ...(meaning.synonyms || []),
            ...((meaning.definitions || []).flatMap(
              (definition) => definition.synonyms || []
            )),
          ]);
          const antonyms = getUniqueTerms([
            ...(meaning.antonyms || []),
            ...((meaning.definitions || []).flatMap(
              (definition) => definition.antonyms || []
            )),
          ]);

          return (
            <div key={`${meaning.partOfSpeech || "meaning"}-${meaningIndex}`}>
              {meaning.partOfSpeech && (
                <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">
                  {meaning.partOfSpeech}
                </div>
              )}
              <ol className="list-decimal space-y-2 pl-5">
                {(meaning.definitions || []).slice(0, 3).map(
                  (definition, definitionIndex) => (
                    <li key={definitionIndex}>
                      <span>{definition.definition}</span>
                      {definition.example && (
                        <div className="mt-1 text-xs italic text-gray-500">
                          {definition.example}
                        </div>
                      )}
                    </li>
                  )
                )}
              </ol>
              {(synonyms.length > 0 || antonyms.length > 0) && (
                <div className="mt-3 space-y-2 text-xs">
                  {synonyms.length > 0 && (
                    <div>
                      <span className="font-semibold text-gray-600">
                        {t("vocab.similar")}
                      </span>{" "}
                      <span className="text-gray-600">
                        {synonyms.join(", ")}
                      </span>
                    </div>
                  )}
                  {antonyms.length > 0 && (
                    <div>
                      <span className="font-semibold text-gray-600">
                        {t("vocab.opposite")}
                      </span>{" "}
                      <span className="text-gray-600">
                        {antonyms.join(", ")}
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {firstSourceUrl && (
        <a
          href={firstSourceUrl}
          target="_blank"
          rel="noreferrer"
          className="mt-4 inline-block text-xs font-medium text-indigo-600 hover:text-indigo-800"
        >
          {t("vocab.viewSource")}
        </a>
      )}
    </div>
  );
};

const SavedVocab: React.FC<SavedVocabProps> = ({ userId }) => {
  const { t } = useLanguage();
  const [vocab, setVocab] = useState<VocabItem[]>([]);
  const [expandedWord, setExpandedWord] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchVocab = async () => {
      try {
        setLoading(true);
        setError(null);

        const { data: vocabData, error: vocabError } = await supabase
          .from("review_vocab")
          .select("word, created_at, updated_at, search_count")
          .eq("user_id", userId)
          .order("search_count", { ascending: false })
          .order("updated_at", { ascending: false });

        if (vocabError) {
          console.error("Error fetching vocabulary:", vocabError);
          setError(t("vocab.loadError"));
          return;
        }

        setVocab(
          (vocabData || []).map((item) => ({
            word: item.word,
            created_at: item.created_at,
            updated_at: item.updated_at,
            search_count: item.search_count,
          }))
        );
      } catch (err) {
        console.error("Unexpected error fetching vocabulary:", err);
        setError(t("vocab.unexpectedError"));
      } finally {
        setLoading(false);
      }
    };

    fetchVocab();
  }, [t, userId]);

  const loadDefinition = async (word: string) => {
    const existingItem = vocab.find((item) => item.word === word);
    if (
      existingItem?.definitionData ||
      existingItem?.definitionLoading ||
      existingItem?.definitionError
    ) {
      return;
    }

    setVocab((prev) =>
      prev.map((item) =>
        item.word === word
          ? { ...item, definitionLoading: true, definitionError: undefined }
          : item
      )
    );

    try {
      const { data, error: definitionError } = await supabase.functions.invoke(
        "getWordDefinition",
        {
          body: {
            word,
            user_id: userId,
          },
        }
      );

      if (definitionError) {
        const message = await getDefinitionErrorMessage(definitionError);
        setVocab((prev) =>
          prev.map((item) =>
            item.word === word
              ? {
                  ...item,
                  definitionLoading: false,
                  definitionError: message,
                }
              : item
          )
        );
        return;
      }

      setVocab((prev) =>
        prev.map((item) =>
          item.word === word
            ? {
                ...item,
                definitionData: data as DictionaryLookupData,
                definitionLoading: false,
                definitionError: undefined,
              }
            : item
        )
      );
    } catch (err) {
      console.error(`Error fetching definition for "${word}":`, err);
      setVocab((prev) =>
        prev.map((item) =>
          item.word === word
            ? {
                ...item,
                definitionLoading: false,
                definitionError: t("vocab.definitionUnavailable"),
              }
            : item
        )
      );
    }
  };

  const handleToggleWord = async (word: string) => {
    if (expandedWord === word) {
      setExpandedWord(null);
      return;
    }

    setExpandedWord(word);
    await loadDefinition(word);
  };

  const handleRemoveWord = async (word: string) => {
    setVocab((prev) =>
      prev.map((item) =>
        item.word === word
          ? { ...item, removing: true, removeError: undefined }
          : item
      )
    );

    const { error: removeError } = await supabase
      .from("review_vocab")
      .delete()
      .eq("user_id", userId)
      .eq("word", word);

    if (removeError) {
      console.error(`Error removing saved vocabulary word "${word}":`, removeError);
      setVocab((prev) =>
        prev.map((item) =>
          item.word === word
            ? {
                ...item,
                removing: false,
                removeError: t("vocab.removeError"),
              }
            : item
        )
      );
      return;
    }

    setVocab((prev) => prev.filter((item) => item.word !== word));
    setExpandedWord((current) => (current === word ? null : current));
  };

  return (
    <div className="mx-auto max-w-5xl p-4 sm:p-6">
      <div className="bg-white p-6 shadow-lg sm:p-8">
        <div className="mb-8">
          <h1
            className="mb-2 text-2xl font-bold text-gray-800 sm:text-3xl"
            tabIndex={-1}
            data-route-focus
          >
            {t("vocab.title")}
          </h1>
          {!loading && !error && (
            <p className="text-gray-600">
              {t("vocab.subtitle", { count: vocab.length })}
            </p>
          )}
        </div>

        {loading ? (
          <div className="text-center">
            <div className="mb-4 text-lg text-gray-600">
              {t("vocab.loading")}
            </div>
            <div className="mx-auto h-8 w-8 animate-spin rounded-full border-b-2 border-blue-600"></div>
          </div>
        ) : error ? (
          <div className="text-center">
            <div className="mb-4 text-lg text-red-600">{error}</div>
            <button
              onClick={() => window.location.reload()}
              className="rounded bg-blue-600 px-6 py-2 text-white transition-colors hover:bg-blue-700"
            >
              {t("common.tryAgain")}
            </button>
          </div>
        ) : vocab.length > 0 ? (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {vocab.map((item) => {
              const isExpanded = expandedWord === item.word;

              return (
                <div
                  key={item.word}
                  className={`border border-gray-200 transition-all ${
                    isExpanded
                      ? "sm:col-span-2 lg:col-span-3"
                      : "hover:border-blue-300 hover:shadow-sm"
                  }`}
                >
                  <div className="flex flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center sm:gap-2">
                    <button
                      type="button"
                      onClick={() => handleToggleWord(item.word)}
                      className="flex min-w-0 flex-1 items-center justify-between gap-4 text-left"
                      aria-expanded={isExpanded}
                    >
                      <span className="min-w-0 truncate text-base font-semibold text-gray-800">
                        {item.word}
                      </span>
                      <span className="shrink-0 rounded-full bg-blue-100 px-2 py-1 text-xs text-blue-800">
                        {t("vocab.lookupCount", { count: item.search_count })}
                      </span>
                    </button>
                    <button
                      type="button"
                      onClick={() => handleRemoveWord(item.word)}
                      disabled={item.removing}
                      className={`shrink-0 self-start rounded px-2 py-1 text-xs font-medium transition-colors sm:self-auto ${
                        item.removing
                          ? "cursor-default text-gray-400"
                          : "text-gray-400 hover:bg-red-50 hover:text-red-600"
                      }`}
                      title={t("vocab.removeTitle")}
                    >
                      {item.removing ? "..." : t("vocab.remove")}
                    </button>
                  </div>

                  {isExpanded && (
                    <div className="px-4 pb-4">
                      {item.removeError && (
                        <div className="border-t border-gray-100 pt-4 text-sm text-red-600">
                          {item.removeError}
                        </div>
                      )}
                      {item.definitionLoading && (
                        <div className="border-t border-gray-100 pt-4 text-sm text-gray-500">
                          {t("vocab.loadingDefinition")}
                        </div>
                      )}
                      {item.definitionError && (
                        <div className="border-t border-gray-100 pt-4 text-sm text-red-600">
                          {item.definitionError}
                        </div>
                      )}
                      {item.definitionData && (
                        <VocabDetail data={item.definitionData} />
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="py-12 text-center">
            <div className="mb-4 text-6xl text-gray-400">📝</div>
            <div className="mb-2 text-lg text-gray-600">
              {t("vocab.emptyTitle")}
            </div>
            <div className="text-sm text-gray-500">
              {t("vocab.emptyBody")}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SavedVocab;
